import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  signIn as cognitoSignIn,
  signUp as cognitoSignUp,
  confirmSignUp as cognitoConfirmSignUp,
  respondToMFAChallenge,
  getCurrentSession,
  getUserAttributes,
  signOut as cognitoSignOut,
} from './cognito';

const AuthContext = createContext(null);

const APP_ENV = process.env.REACT_APP_ENV || 'prod';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState(null);
  const [role, setRole] = useState(null);
  const [status, setStatus] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Fetch and set user attributes
  const syncAttributes = useCallback(async () => {
    const attrs = await getUserAttributes();
    if (attrs) {
      setEmail(attrs.email);
      setRole(attrs.role);
      setStatus(attrs.status);
      setPlan(attrs.plan);
    }
    return attrs;
  }, []);

  // Check existing session on mount
  useEffect(() => {
    getCurrentSession()
      .then(async (result) => {
        if (result) {
          setUser(result.user);
          setIsAuthenticated(true);
          await syncAttributes();
        }
      })
      .finally(() => setLoading(false));
  }, [syncAttributes]);

  const login = useCallback(async (emailInput, password) => {
    const result = await cognitoSignIn(emailInput, password);

    if (result.mfaRequired || result.mfaSetup) {
      return result;
    }

    // Fetch attributes before confirming auth
    setUser(result.user);
    const attrs = await getUserAttributes();
    const userRole = attrs?.role || 'user';
    const userStatus = attrs?.status || 'active';

    // Dev environment: only admins allowed
    if (APP_ENV === 'dev' && userRole !== 'admin') {
      cognitoSignOut();
      throw new Error('This environment is restricted to admin users.');
    }

    // Suspended / inactive: block login
    if (userStatus === 'suspended' || userStatus === 'inactive') {
      cognitoSignOut();
      const msg = userStatus === 'suspended'
        ? 'Your account has been suspended. Please contact support.'
        : 'Your account is inactive. Please contact support.';
      throw new Error(msg);
    }

    setIsAuthenticated(true);
    setEmail(attrs?.email || emailInput);
    setRole(userRole);
    setStatus(userStatus);
    setPlan(attrs?.plan || 'free');
    return result;
  }, []);

  const submitMFA = useCallback(async (cognitoUser, code, mfaType) => {
    const result = await respondToMFAChallenge(cognitoUser, code, mfaType);
    setUser(result.user);

    const attrs = await getUserAttributes();
    const userRole = attrs?.role || 'user';
    const userStatus = attrs?.status || 'active';

    if (APP_ENV === 'dev' && userRole !== 'admin') {
      cognitoSignOut();
      throw new Error('This environment is restricted to admin users.');
    }

    if (userStatus === 'suspended' || userStatus === 'inactive') {
      cognitoSignOut();
      throw new Error('Your account has been suspended. Please contact support.');
    }

    setIsAuthenticated(true);
    setEmail(attrs?.email);
    setRole(userRole);
    setStatus(userStatus);
    setPlan(attrs?.plan || 'free');
    return result;
  }, []);

  const register = useCallback(async ({ email: emailInput, password, invitationCode }) => {
    return cognitoSignUp({ email: emailInput, password, invitationCode });
  }, []);

  const confirmRegistration = useCallback(async (emailInput, code) => {
    return cognitoConfirmSignUp(emailInput, code);
  }, []);

  const logout = useCallback(() => {
    cognitoSignOut();
    setUser(null);
    setEmail(null);
    setRole(null);
    setStatus(null);
    setPlan(null);
    setIsAuthenticated(false);
  }, []);

  const isAdmin = role === 'admin';
  const isActive = status === 'active';
  const isOverdue = status === 'overdue';

  const value = {
    user,
    email,
    role,
    status,
    plan,
    loading,
    isAuthenticated,
    isAdmin,
    isActive,
    isOverdue,
    login,
    submitMFA,
    register,
    confirmRegistration,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
